import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MainHeader } from "@/components/layout/MainHeader";
import { Helmet } from "react-helmet";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy - Mai@ Email Assistant</title>
        <meta name="description" content="Privacy Policy for Mai@ Email Assistant. Learn about how we handle your data, our zero-storage policy, and our commitment to your privacy." />
        <meta property="og:title" content="Privacy Policy - Mai@ Email Assistant" />
        <meta property="og:description" content="Privacy Policy for Mai@ Email Assistant. Learn about how we handle your data, our zero-storage policy, and our commitment to your privacy." />
        <meta property="og:url" content="https://maia-email.com/privacy-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/maia-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - Mai@ Email Assistant" />
        <meta name="twitter:description" content="Privacy Policy for Mai@ Email Assistant. Learn about how we handle your data, our zero-storage policy, and our commitment to your privacy." />
        <meta name="twitter:image" content="/maia-og.jpg" />
        <link rel="canonical" href="https://maia-email.com/privacy-policy" />
      </Helmet>

      <MainHeader />
      <div className="container mx-auto py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="prose prose-sm md:prose-base lg:prose-lg dark:prose-invert max-w-none">
          <h1>Privacy Policy</h1>
          <p>Last updated: {new Date().toLocaleDateString()}</p>

          <h2>1. Introduction</h2>
          <p>
            Welcome to Maia Email Assistant Web Service. We respect your privacy and are committed to protecting your personal data. 
            This privacy policy explains how we handle your data when you use our email assistance service.
          </p>

          <h2>2. Data We Collect</h2>
          <h3>2.1 Essential Data</h3>
          <ul>
            <li>Email address (for authentication purposes)</li>
            <li>Basic profile information from Google (when you connect your Gmail account)</li>
          </ul>

          <h3>2.2 Service Usage Data</h3>
          <ul>
            <li>Documents and files you upload for AI processing</li>
            <li>Email content that you choose to process through our service</li>
          </ul>

          <h2>3. How We Use Your Data</h2>
          <p>We use your data for the following purposes:</p>
          <ul>
            <li>To provide and maintain our service</li>
            <li>To authenticate you and manage your account</li>
            <li>To process your documents and emails through OpenAI's services</li>
            <li>To communicate with you about service-related matters</li>
          </ul>

          <h2>4. Data Storage and Third-Party Services</h2>
          <h3>4.1 Supabase</h3>
          <p>
            We store minimal user data in Supabase, limited to:
          </p>
          <ul>
            <li>Your email address</li>
            <li>Authentication tokens</li>
          </ul>

          <h3>4.2 OpenAI</h3>
          <p>
            Documents and content processed by our service are sent to OpenAI. Please note:
          </p>
          <ul>
            <li>We are not responsible for the content of AI-generated responses</li>
            <li>Documents uploaded to OpenAI are subject to OpenAI's privacy policy</li>
            <li>We provide functionality to delete your documents through OpenAI's API upon request</li>
          </ul>

          <h2>5. Your Rights</h2>
          <p>Under GDPR and applicable laws, you have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to processing of your data</li>
            <li>Request transfer of your data</li>
            <li>Withdraw consent at any time</li>
          </ul>

          <h2>6. Data Deletion</h2>
          <p>
            You can request deletion of your data at any time. This includes:
          </p>
          <ul>
            <li>Account deletion through the settings page</li>
            <li>Deletion of documents uploaded to OpenAI</li>
            <li>Removal of Gmail connection and associated tokens</li>
          </ul>

          <h2>7. Data Security</h2>
          <p>
            We implement appropriate security measures to protect your data, including:
          </p>
          <ul>
            <li>Secure authentication through Supabase</li>
            <li>Encrypted data transmission</li>
            <li>Regular security updates and monitoring</li>
          </ul>

          <h2>8. Children's Privacy</h2>
          <p>
            Our service is not intended for children under 13 years of age, and we do not knowingly collect data from children under 13.
          </p>

          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
          </p>

          <h2>10. Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our data practices, please contact us at:
          </p>
          
          <div className="bg-secondary/20 p-6 rounded-lg my-4">
            <h3 className="text-lg font-semibold mb-2">Company Information</h3>
            <div className="space-y-2">
              <p className="font-medium">MLC CONSULTING - UNMANNED EXPERT S.L.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-muted-foreground">Tax ID (CIF)</p>
                  <p className="font-medium">B40577181</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <a href="mailto:info@mlcconsulting.es" className="text-primary hover:underline">
                    info@mlcconsulting.es
                  </a>
                </div>
              </div>
              <div>
                <p className="text-muted-foreground">Registered Address</p>
                <p className="font-medium">
                  Carrer Les Masses 13<br />
                  46117 Betera (Valencia)<br />
                  SPAIN
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;